// src/components/PostCard.tsx
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { PostData } from '../types/reddit';
import { getShowSelftextPreviewSetting } from '../utils/settings'; // Import setting getter

// Helper to format time difference (assuming it exists or create it)
const timeAgo = (timestamp: number): string => {
    const now = Date.now();
    const secondsPast = Math.floor((now - timestamp * 1000) / 1000);
    if (secondsPast < 60) return `${secondsPast}s ago`;
    if (secondsPast < 3600) return `${Math.floor(secondsPast / 60)}m ago`;
    if (secondsPast <= 86400) return `${Math.floor(secondsPast / 3600)}h ago`;
    const days = Math.floor(secondsPast / 86400);
    return `${days}d ago`;
};

// Helper to decode HTML entities (needed for plain text rendering)
function decodeHtmlEntities(text: string | undefined | null): string {
    if (!text) return "";
    try {
        const textarea = document.createElement('textarea');
        textarea.innerHTML = text;
        return textarea.value || text; // Fallback to original if decoding fails
    } catch (e) {
        console.warn("Could not decode HTML entities:", text, e);
        // Basic fallback replacements if DOM is unavailable or fails
        return text
            .replace(/</g, '<')
            .replace(/>/g, '>')
            .replace(/"/g, '"')
            .replace(/'/g, "'")
            .replace(/&/g, '&');
    }
}


interface PostCardProps {
    post: PostData;
    isHomePage?: boolean; // To conditionally show subreddit link
    isNsfwAllowedForSubreddit: boolean; // To handle NSFW thumbnail blur
}

const PostCard: React.FC<PostCardProps> = ({ post, isHomePage = false, isNsfwAllowedForSubreddit }) => {
    const [showPreview, setShowPreview] = useState(false);

    // Load setting on mount
    useEffect(() => {
        setShowPreview(getShowSelftextPreviewSetting());
    }, []);

    const {
        title, author, subreddit_name_prefixed, score, created_utc, num_comments,
        permalink, thumbnail, thumbnail_width, thumbnail_height, is_video,
        over_18, is_self, selftext, domain 
    } = post;

    const isNsfw = over_18;
    const showThumbnailBlur = isNsfw && !isNsfwAllowedForSubreddit;

    // Determine if thumbnail is a valid image URL
    const isValidThumbnail = thumbnail && !['self', 'default', 'spoiler', 'nsfw', ''].includes(thumbnail);
    const aspectRatio = (thumbnail_width && thumbnail_height) ? thumbnail_width / thumbnail_height : 1; // Default to square if dimensions unknown

    // Check if selftext exists and preview is enabled
    const shouldShowSelftext = showPreview && is_self && selftext && selftext.trim().length > 0;
    const decodedSelftext = decodeHtmlEntities(selftext); // Decode for plain text display

    return (
        <article className="p-4 md:p-5 bg-white dark:bg-gray-800/50 rounded-lg border border-gray-200 dark:border-gray-700/50 shadow-sm hover:border-gray-300 dark:hover:border-gray-600 transition-colors duration-150">
            <Link to={permalink} className="flex gap-4 items-start">
                {/* Main Content Area */}
                <div className="flex-1 min-w-0">
                    {/* Post Title */}
                    <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-1.5 leading-tight hover:text-indigo-700 dark:hover:text-indigo-400 transition-colors duration-150">
                        {title}
                        {isNsfw && <span className="ml-1.5 text-[9px] font-bold text-red-600 dark:text-red-400 border border-red-500 dark:border-red-400 px-0.5 rounded-sm leading-none align-middle">NSFW</span>}
                    </h2>

                    {/* Post Metadata */}
                    <div className={`text-xs text-gray-500 dark:text-gray-400 space-x-2 flex flex-wrap items-center gap-x-2 gap-y-1${shouldShowSelftext ? ' mb-3' : ''}`}>
                        <span>{score} points</span>
                        <span aria-hidden="true">•</span>
                        <span>{num_comments} comments</span>
                        <span aria-hidden="true">•</span>
                        <span title={new Date(created_utc * 1000).toLocaleString()}>{timeAgo(created_utc)}</span>
                        <span aria-hidden="true">•</span>
                        <span>by u/{author}</span>
                        {/* Conditionally show subreddit link */}
                        {isHomePage && (
                            <>
                                <span aria-hidden="true">•</span>
                                <Link
                                    to={`/${subreddit_name_prefixed}`}
                                    onClick={(e) => e.stopPropagation()} // Prevent card link trigger
                                    className="font-medium hover:underline text-gray-600 dark:text-gray-300"
                                >
                                    {subreddit_name_prefixed}
                                </Link>
                            </>
                        )}
                         {/* Show domain for link posts */}
                         {!is_self && domain && (
                             <>
                                <span aria-hidden="true">•</span>
                                <span className="text-gray-400 dark:text-gray-500 truncate">({domain})</span>
                             </>
                         )}
                    </div>

                    {/* Selftext Preview - NEW */}
                    {shouldShowSelftext && (
                        <div className="mt-3 relative text-sm text-gray-700 dark:text-gray-300 leading-relaxed max-h-[4.5rem] overflow-hidden"> {/* max-h approx 3 lines */}
                            <p className="line-clamp-3">
                                {decodedSelftext}
                            </p>
                            {/* Fade-out effect using pseudo-element */}
                            <div className="absolute bottom-0 left-0 right-0 h-6 bg-gradient-to-t from-white dark:from-gray-800/50 to-transparent pointer-events-none"></div>
                        </div>
                    )}

                </div>

                {/* Thumbnail Area */}
                {isValidThumbnail && (
                    <div className={`relative flex-shrink-0 w-16 h-16 md:w-20 md:h-20 rounded overflow-hidden border border-gray-100 dark:border-gray-700/50 ${showThumbnailBlur ? 'blur-sm' : ''}`}>
                        <img
                            src={thumbnail}
                            alt="" // Decorative, title is main link content
                            loading="lazy"
                            className={`w-full h-full object-cover ${is_video ? 'brightness-90' : ''}`} // Keep aspect ratio via object-cover
                            style={{ aspectRatio: `${aspectRatio}` }} // Hint aspect ratio
                            onError={(e) => { e.currentTarget.style.display = 'none'; }} // Hide on error
                        />
                        {/* Video Indicator */}
                        {is_video && (
                            <div className="absolute inset-0 flex items-center justify-center bg-black/30">
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-white/80" viewBox="0 0 20 20" fill="currentColor">
                                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM9.555 7.168A1 1 0 008 8v4a1 1 0 001.555.832l3-2a1 1 0 000-1.664l-3-2z" clipRule="evenodd" />
                                </svg>
                            </div>
                        )}
                    </div>
                )}
            </Link>
        </article>
    );
};

export default PostCard;